import { readJsonPref, writeJsonPref } from '@utils/localJsonPref';
import { DEFAULT_FILTERS } from './activitiesFilter';
import { DEFAULT_PAGE_FILTERS, type IPageFilters } from './pageFilters';
import type { ListItemKind } from './model/listItem';

/**
 * Persist the Activities filter set across leaving/returning to the page. Global
 * (not per-site) — a filter like "critical only" is a viewing preference that
 * carries across sites. The store guarding lives in the shared localJsonPref
 * helper; every field is validated on read so a stale/renamed key can't yield an
 * invalid filter.
 */
const KEY = 'dorca.activities.filters';

function sanitize(parsed: unknown): IPageFilters {
  if (!parsed || typeof parsed !== 'object') return DEFAULT_PAGE_FILTERS;
  const raw = parsed as Record<string, unknown>;
  return {
    // The page's date window (its date picker) replaced the date scope: a
    // preset stored by the old picker is dropped, never restored.
    date: DEFAULT_FILTERS.date,
    types: Array.isArray(raw.types)
      ? raw.types.filter((x): x is ListItemKind => x === 'activity' || x === 'issue')
      : DEFAULT_FILTERS.types,
    critical: typeof raw.critical === 'boolean' ? raw.critical : false,
    criticalPath: typeof raw.criticalPath === 'boolean' ? raw.criticalPath : false,
    milestone: typeof raw.milestone === 'boolean' ? raw.milestone : false,
    safety: typeof raw.safety === 'boolean' ? raw.safety : false,
    overdue: typeof raw.overdue === 'boolean' ? raw.overdue : false,
    checksOutstanding: typeof raw.checksOutstanding === 'boolean' ? raw.checksOutstanding : false,
    professions: Array.isArray(raw.professions)
      ? raw.professions.filter((x): x is string => typeof x === 'string')
      : DEFAULT_FILTERS.professions,
    // The Status checkboxes replaced the single status: a stored one is
    // dropped, the hidden set is read (the default when absent).
    status: DEFAULT_FILTERS.status,
    hiddenStatuses: Array.isArray(raw.hiddenStatuses)
      ? raw.hiddenStatuses.filter((x): x is string => typeof x === 'string')
      : DEFAULT_PAGE_FILTERS.hiddenStatuses,
    search: typeof raw.search === 'string' ? raw.search : DEFAULT_FILTERS.search,
  };
}

export function readFilters(): IPageFilters {
  const parsed = readJsonPref(KEY);
  return parsed === undefined ? DEFAULT_PAGE_FILTERS : sanitize(parsed);
}

export function writeFilters(filters: IPageFilters): void {
  writeJsonPref(KEY, filters);
}

/**
 * The filter PANEL's open/collapsed choice — its own key beside the filter
 * values above so clearing one never clobbers the other. Global like the
 * filters (a layout preference, not per-site). DEFAULT OPEN: the panel is
 * page furniture, and first-time users should see the filter options exist.
 * Anything but a stored boolean falls back to open.
 */
const PANEL_OPEN_KEY = 'dorca.activities.filtersPanelOpen';

export function readFiltersPanelOpen(): boolean {
  const parsed = readJsonPref(PANEL_OPEN_KEY);
  return typeof parsed === 'boolean' ? parsed : true;
}

export function writeFiltersPanelOpen(open: boolean): void {
  writeJsonPref(PANEL_OPEN_KEY, open);
}
